import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Map as MaplibreMap,
  Popup,
  type GeoJSONSource,
  type GeoJSONSourceSpecification,
  type LineLayerSpecification,
  type FillLayerSpecification,
  type CircleLayerSpecification,
  type SymbolLayerSpecification,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  Loader2Icon,
  MapIcon,
  LayersIcon,
  XIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  Building2Icon,
  BoxIcon,
  SmartphoneIcon,
  SlidersHorizontalIcon,
  AlertCircleIcon,
  RotateCcwIcon,
  MapPinIcon,
  WheatIcon,
} from 'lucide-react';
import {
  useGetCivicRegions,
  useGetCivicCategories,
  useGetCivicTenants,
  useGetGeocodeSearch,
  getGetMapIssuesQueryOptions,
  getGetMapAssetsQueryOptions,
  getGetRegionSummaryQueryOptions,
  type RegionFeatureCollection,
  type IssueFeatureCollection,
  type AssetFeatureCollection,
  type RegionSummary,
} from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ZoomPanControls, type PanDirection } from '@/components/ui/zoom-pan-controls';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { createOsmMap, resizeOsmMap } from '@/components/map/base-map';
import { getStatusConfig, SEVERITY_LABELS, formatRelativeTime } from '@/lib/report-helpers';
import { useHuntChallenges } from '@/hooks/use-hunt-challenges';
import { getFarmAddress, resolveRegionKind } from '@/lib/registered-farms';
import {
  HOME_RENTAL_PROPERTIES,
  type HomeRentalProperty,
} from '@/lib/home-rental-properties';
import { getPurposeBySlug } from '@/lib/purposes';

// ── Constants ───────────────────────────────────────────────────────────────

/** Zoomed-out Australia fallback — used when no URL coords and geolocation is unavailable. */
const DEFAULT_CENTER: [number, number] = [134.0, -25.3]; // Australia
const DEFAULT_ZOOM = 3.5;
/** Default zoom when deep-linking to a point (coords or geocoded address). */
const LOCATION_FRAME_ZOOM = 15;
/** Zoom level when flying to the user's detected geolocation. */
const GEOLOCATION_ZOOM = 14;
const LOCATION_FRAME_DURATION_MS = 900;

const COUNCIL_BOUNDARY_COLOR = '#6366f1';
const FARM_BOUNDARY_COLOR = '#b45309';

type RegionalMapScope = 'council' | 'farm';

function getRegionalMapScope(purposeSlug: string): RegionalMapScope | null {
  if (purposeSlug === 'councils') return 'council';
  if (purposeSlug === 'farms') return 'farm';
  return null;
}

/** Australia-wide fallback bbox so overlay queries work on initial load before a location is resolved. */
const DEFAULT_BBOX: Bbox = {
  minLng: 112,
  minLat: -44,
  maxLng: 154,
  maxLat: -10,
};

interface MapPoint {
  lng: number;
  lat: number;
}

/** Parse a finite number from a query param (empty/invalid → null). */
function parseQueryNumber(raw: string | null | undefined): number | null {
  if (raw == null || raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Read lat/lng from common query aliases:
 * lat|latitude + lng|lon|longitude
 */
function parseLatLngFromSearch(searchParams: URLSearchParams): MapPoint | null {
  const lat = parseQueryNumber(
    searchParams.get('lat') ?? searchParams.get('latitude'),
  );
  const lng = parseQueryNumber(
    searchParams.get('lng')
      ?? searchParams.get('lon')
      ?? searchParams.get('longitude'),
  );
  if (lat === null || lng === null) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

function parseZoomFromSearch(searchParams: URLSearchParams, fallback: number): number {
  const z = parseQueryNumber(searchParams.get('zoom'));
  if (z === null || z < 1 || z > 22) return fallback;
  return z;
}

/** Rough viewport bbox around a point so overlay queries start near the target. */
function bboxAroundPoint(point: MapPoint, delta = 0.05): Bbox {
  return {
    minLng: point.lng - delta,
    minLat: point.lat - delta * 0.75,
    maxLng: point.lng + delta,
    maxLat: point.lat + delta * 0.75,
  };
}

function flyMapToPoint(
  map: MaplibreMap,
  point: MapPoint,
  zoom: number,
): void {
  map.easeTo({
    center: [point.lng, point.lat],
    zoom,
    duration: LOCATION_FRAME_DURATION_MS,
  });
}

const STATUS_COLORS: Record<string, string> = {
  submitted: '#ef4444',
  acknowledged: '#f97316',
  in_progress: '#3b82f6',
  scheduled: '#6366f1',
  resolved: '#22c55e',
  closed: '#16a34a',
  rejected: '#9ca3af',
  duplicate: '#d1d5db',
};

const STATUS_GROUPS = [
  { label: 'Open', colors: ['#ef4444', '#f97316'], statuses: ['submitted', 'acknowledged'] },
  { label: 'In Progress', colors: ['#3b82f6', '#6366f1'], statuses: ['in_progress', 'scheduled'] },
  { label: 'Resolved', colors: ['#22c55e', '#16a34a'], statuses: ['resolved', 'closed'] },
  { label: 'Closed', colors: ['#9ca3af', '#d1d5db'], statuses: ['rejected', 'duplicate'] },
];

const ALL_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'duplicate', label: 'Duplicate' },
];

interface Bbox {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

/** Expand ring coordinates into a mutable bbox accumulator. */
function expandBounds(
  coords: number[][],
  acc: { minLng: number; minLat: number; maxLng: number; maxLat: number },
): void {
  for (const pair of coords) {
    const lng = pair[0];
    const lat = pair[1];
    if (typeof lng !== 'number' || typeof lat !== 'number') continue;
    if (lng < acc.minLng) acc.minLng = lng;
    if (lat < acc.minLat) acc.minLat = lat;
    if (lng > acc.maxLng) acc.maxLng = lng;
    if (lat > acc.maxLat) acc.maxLat = lat;
  }
}

/** Compute [[west, south], [east, north]] for a Polygon/MultiPolygon boundary. */
function boundsFromBoundary(boundary: unknown): [[number, number], [number, number]] | null {
  if (!boundary || typeof boundary !== 'object') return null;
  const g = boundary as { type?: string; coordinates?: unknown };
  const acc = {
    minLng: Infinity,
    minLat: Infinity,
    maxLng: -Infinity,
    maxLat: -Infinity,
  };

  if (g.type === 'Polygon' && Array.isArray(g.coordinates)) {
    const ring = g.coordinates[0];
    if (Array.isArray(ring)) expandBounds(ring as number[][], acc);
  } else if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
    for (const poly of g.coordinates) {
      if (!Array.isArray(poly)) continue;
      const ring = poly[0];
      if (Array.isArray(ring)) expandBounds(ring as number[][], acc);
    }
  } else {
    return null;
  }

  if (!Number.isFinite(acc.minLng) || !Number.isFinite(acc.minLat)) return null;
  return [
    [acc.minLng, acc.minLat],
    [acc.maxLng, acc.maxLat],
  ];
}

const REGION_FRAME_PADDING = { top: 80, bottom: 120, left: 48, right: 48 } as const;
const REGION_FRAME_MAX_ZOOM = 15.5;
const REGION_FRAME_DURATION_MS = 900;

/** Pan/zoom the map to a region boundary (polygon) or point location. */
function frameGeometryOnMap(map: MaplibreMap, boundary: unknown): boolean {
  const bounds = boundsFromBoundary(boundary);
  if (bounds) {
    map.fitBounds(bounds, {
      padding: REGION_FRAME_PADDING,
      maxZoom: REGION_FRAME_MAX_ZOOM,
      duration: REGION_FRAME_DURATION_MS,
    });
    return true;
  }

  if (!boundary || typeof boundary !== 'object') return false;
  const g = boundary as { type?: string; coordinates?: unknown };
  if (g.type !== 'Point' || !Array.isArray(g.coordinates) || g.coordinates.length < 2) {
    return false;
  }
  const lng = g.coordinates[0];
  const lat = g.coordinates[1];
  if (typeof lng !== 'number' || typeof lat !== 'number') return false;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  // Placeholder geometry used when a region has no real coordinates yet.
  if (lng === 0 && lat === 0) return false;

  map.easeTo({
    center: [lng, lat],
    zoom: Math.max(map.getZoom(), 14),
    duration: REGION_FRAME_DURATION_MS,
  });
  return true;
}

function toRegionCollection(
  data: RegionFeatureCollection | undefined,
  tenantById: Map<string, { type?: string; slug?: string }>,
) {
  return {
    type: 'FeatureCollection' as const,
    features: (data?.features ?? []).map((f) => {
      const tenantId = f.properties?.tenantId ?? '';
      const tenant = tenantById.get(tenantId);
      const slug = f.properties?.slug ?? '';
      const name = f.properties?.name ?? '';
      const regionKind = resolveRegionKind(slug, name, tenant?.type, tenant?.slug);
      return {
        type: 'Feature' as const,
        geometry: f.geometry ?? { type: 'Point' as const, coordinates: [0, 0] },
        properties: {
          id: f.properties?.id ?? '',
          name,
          tenantId,
          slug,
          regionKind,
          tenantType: tenant?.type ?? '',
          tenantSlug: tenant?.slug ?? '',
        },
      };
    }),
  };
}

function buildRegionLayerFilter(showCouncils: boolean, showFarms: boolean): unknown[] {
  const kinds: string[] = [];
  if (showCouncils) kinds.push('council');
  if (showFarms) kinds.push('farm');
  if (kinds.length === 0) {
    // Hide all region features when both layers are off.
    return ['==', ['get', 'id'], ''];
  }
  if (kinds.length === 1) {
    return ['==', ['get', 'regionKind'], kinds[0]!];
  }
  return ['in', ['get', 'regionKind'], ['literal', kinds]];
}

function toIssueCollection(data: IssueFeatureCollection | undefined) {
  return {
    type: 'FeatureCollection' as const,
    features: data?.features ?? [],
  };
}

function toAssetCollection(data: AssetFeatureCollection | undefined) {
  return {
    type: 'FeatureCollection' as const,
    features: data?.features ?? [],
  };
}

function toHomeRentalPropertyCollection(properties: readonly HomeRentalProperty[]) {
  return {
    type: 'FeatureCollection' as const,
    features: properties.map((property) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [property.longitude, property.latitude],
      },
      properties: {
        id: property.id,
        name: property.name,
        address: property.address,
        geocodePrecision: property.geocodePrecision,
      },
    })),
  };
}

const EMPTY_HOME_RENTAL_PROPERTIES: readonly HomeRentalProperty[] = [];

// ── Component ───────────────────────────────────────────────────────────────

export function MapExplorerPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters from URL
  const statusFilter = searchParams.get('status') ?? 'all';
  const categoryFilter = searchParams.get('category') ?? 'all';
  const tenantFilter = searchParams.get('tenant') ?? 'all';
  const purposeContext = getPurposeBySlug(searchParams.get('purpose') ?? '');
  const regionalMapScope = getRegionalMapScope(purposeContext?.slug ?? '');
  const homeRentalProperties = useMemo(
    () => purposeContext?.slug === 'homes-rentals'
      ? HOME_RENTAL_PROPERTIES
      : EMPTY_HOME_RENTAL_PROPERTIES,
    [purposeContext?.slug],
  );
  const dateFrom = searchParams.get('from') ?? '';
  const dateTo = searchParams.get('to') ?? '';

  // Filters from URL — region deep-link (id or slug) for farm "View on Map" CTAs.
  const regionParam = searchParams.get('region') ?? '';
  const regionSlugParam = searchParams.get('regionSlug') ?? '';
  // Point deep-link: /map?lat=-37.8&lng=144.9[&zoom=15] or /map?address=...
  const addressParam = (searchParams.get('address') ?? '').trim();
  const urlCoords = useMemo(() => parseLatLngFromSearch(searchParams), [searchParams]);
  const urlZoom = useMemo(
    () => parseZoomFromSearch(searchParams, LOCATION_FRAME_ZOOM),
    [searchParams],
  );
  const hasRegionDeepLink = Boolean(regionParam || regionSlugParam);
  // Geocode address only when no explicit coords/region deep-link is present.
  const shouldGeocodeAddress =
    !hasRegionDeepLink && !urlCoords && addressParam.length >= 3;

  // UI state — start near a URL point when provided so overlays fetch without waiting on map load.
  const [bbox, setBbox] = useState<Bbox>(() =>
    urlCoords ? bboxAroundPoint(urlCoords) : DEFAULT_BBOX,
  );
  const [showAssets, setShowAssets] = useState(false);
  const [showCouncilBoundaries, setShowCouncilBoundaries] = useState(
    () => regionalMapScope !== 'farm',
  );
  const [showFarmBoundaries, setShowFarmBoundaries] = useState(
    () => regionalMapScope !== 'council',
  );
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapInitKey, setMapInitKey] = useState(0);
  /** Bumps when MapLibre layers are ready so deep-link framing can retry after init. */
  const [mapReadyVersion, setMapReadyVersion] = useState(0);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [legendExpanded, setLegendExpanded] = useState(false);
  /** User's current geolocation — used as fallback when no URL coords/address/region deep-link is present. */
  const [userLocation, setUserLocation] = useState<MapPoint | null>(null);

  // Map refs
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const moveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popupRef = useRef<Popup | null>(null);
  // Latest overlay payloads — applied on style load and whenever data arrives.
  const regionsDataRef = useRef<RegionFeatureCollection | undefined>(undefined);
  const issuesDataRef = useRef<IssueFeatureCollection | undefined>(undefined);
  const assetsDataRef = useRef<AssetFeatureCollection | undefined>(undefined);
  const homeRentalPropertiesRef = useRef<readonly HomeRentalProperty[]>([]);
  const showAssetsRef = useRef(false);
  const showCouncilBoundariesRef = useRef(true);
  const showFarmBoundariesRef = useRef(true);
  const tenantByIdRef = useRef<Map<string, { type?: string; slug?: string }>>(new Map());
  const fittedRegionIdRef = useRef<string | null>(null);
  /** Dedupes on-load fly-to for lat/lng or geocoded address deep-links. */
  const fittedLocationKeyRef = useRef<string | null>(null);

  // Manual recovery: clears the error and re-runs map initialization
  // without a full page reload.
  const handleReloadMap = useCallback(() => {
    setMapError(null);
    setMapInitKey((k) => k + 1);
  }, []);

  const handleMapZoom = useCallback((delta: 1 | -1) => {
    const map = mapRef.current;
    if (!map) return;
    const options = { duration: 250 };
    if (delta > 0) {
      map.zoomIn(options);
      return;
    }
    map.zoomOut(options);
  }, []);

  const handleMapPan = useCallback((direction: PanDirection) => {
    const map = mapRef.current;
    if (!map) return;

    const distance = 120;
    const offset: [number, number] = direction === 'up'
      ? [0, -distance]
      : direction === 'down'
        ? [0, distance]
        : direction === 'left'
          ? [-distance, 0]
          : [distance, 0];
    map.panBy(offset, { duration: 250 });
  }, []);

  const handleMapReset = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo({
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      duration: LOCATION_FRAME_DURATION_MS,
    });
  }, []);

  // Hunt challenge: map_find target asset detection
  const { incompleteByType, completeChallenge: completeHuntChallenge } = useHuntChallenges();
  const mapFindChallenge = incompleteByType.map_find;
  const targetAssetId = mapFindChallenge?.config?.targetAssetId ?? null;
  const targetAssetIdRef = useRef<string | null>(null);
  const completeHuntChallengeRef = useRef<(challengeId: string, evidence?: { foundAssetId?: string }) => void>(() => {});
  const incompleteByTypeRef = useRef<{ map_find?: { id: string } }>({});
  targetAssetIdRef.current = targetAssetId;
  completeHuntChallengeRef.current = completeHuntChallenge;
  incompleteByTypeRef.current = { map_find: mapFindChallenge ? { id: mapFindChallenge.id } : undefined };

  // ── Data fetching ──────────────────────────────────────────────────────

  const { data: regionsData, isFetching: regionsFetching } = useGetCivicRegions();
  const { data: categoriesData } = useGetCivicCategories();
  const { data: tenantsData } = useGetCivicTenants();
  const { data: geocodeData, isFetching: geocodeFetching } = useGetGeocodeSearch(
    { q: addressParam },
    {
      query: {
        queryKey: ['geocode', 'search', addressParam],
        enabled: shouldGeocodeAddress,
        staleTime: 60_000,
        retry: false,
      },
    },
  );

  // Geolocation fallback: when no URL coords/address/region deep-link is provided,
  // try to detect the user's current location so the map can fly there on load.
  useEffect(() => {
    if (hasRegionDeepLink || urlCoords || addressParam) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        // Permission denied or unavailable — stay on the zoomed-out Australia default.
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300_000 },
    );
  }, [hasRegionDeepLink, urlCoords, addressParam]);

  const deepLinkLocation = useMemo(() => {
    if (hasRegionDeepLink) return null;
    if (urlCoords) {
      return {
        point: urlCoords,
        zoom: urlZoom,
        key: `coords:${urlCoords.lat},${urlCoords.lng}:${urlZoom}`,
      };
    }
    if (!shouldGeocodeAddress) {
      // Geolocation fallback — only when no explicit URL coords/address.
      if (userLocation) {
        return {
          point: userLocation,
          zoom: GEOLOCATION_ZOOM,
          key: `geo:${userLocation.lat},${userLocation.lng}`,
        };
      }
      return null;
    }
    const hit = geocodeData?.results?.[0];
    if (
      !hit
      || !Number.isFinite(hit.latitude)
      || !Number.isFinite(hit.longitude)
    ) {
      return null;
    }
    const point = { lat: hit.latitude, lng: hit.longitude };
    return {
      point,
      zoom: urlZoom,
      key: `address:${addressParam}:${point.lat},${point.lng}:${urlZoom}`,
    };
  }, [
    hasRegionDeepLink,
    urlCoords,
    urlZoom,
    shouldGeocodeAddress,
    geocodeData,
    addressParam,
    userLocation,
  ]);

  const categories = useMemo(() => categoriesData?.categories ?? [], [categoriesData]);
  const scopedCategories = useMemo(() => {
    if (!purposeContext) return categories;
    return categories.filter((category) => {
      const contexts = category.contexts ?? [];
      return contexts.length === 0 || contexts.includes(purposeContext.slug);
    });
  }, [categories, purposeContext]);
  const scopedCategorySlugs = useMemo(
    () => new Set(scopedCategories.map((category) => category.slug).filter(Boolean)),
    [scopedCategories],
  );
  const effectiveCategoryFilter = scopedCategories.some((category) => category.id === categoryFilter)
    ? categoryFilter
    : 'all';
  const tenants = useMemo(() => tenantsData?.tenants ?? [], [tenantsData]);
  const effectiveShowAssets = regionalMapScope === null && showAssets;
  const effectiveShowCouncilBoundaries = regionalMapScope !== 'farm' && showCouncilBoundaries;
  const effectiveShowFarmBoundaries = regionalMapScope !== 'council' && showFarmBoundaries;
  const tenantById = useMemo(() => {
    const map = new Map<string, { type?: string; slug?: string }>();
    for (const t of tenants) {
      if (t.id) map.set(t.id, { type: t.type, slug: t.slug });
    }
    return map;
  }, [tenants]);

  // Issues query (bbox-filtered)
  // Filters are optional overlays only — basemap + default bbox always fetch.
  const issuesParams = useMemo(() => ({
    minLng: bbox.minLng,
    minLat: bbox.minLat,
    maxLng: bbox.maxLng,
    maxLat: bbox.maxLat,
    categoryId: effectiveCategoryFilter !== 'all' ? effectiveCategoryFilter : undefined,
    status: statusFilter !== 'all' ? (statusFilter as 'submitted' | 'acknowledged' | 'in_progress' | 'scheduled' | 'resolved' | 'closed' | 'rejected' | 'duplicate') : undefined,
    tenantId: tenantFilter !== 'all' ? tenantFilter : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [bbox, effectiveCategoryFilter, statusFilter, tenantFilter, dateFrom, dateTo]);

  const { data: issuesData, isFetching: issuesFetching } = useQuery({
    ...getGetMapIssuesQueryOptions(issuesParams),
    staleTime: 30_000,
  });

  // Assets query (bbox-filtered) — only when the layer toggle is on
  const assetsParams = useMemo(() => ({
    minLng: bbox.minLng,
    minLat: bbox.minLat,
    maxLng: bbox.maxLng,
    maxLat: bbox.maxLat,
    tenantId: tenantFilter !== 'all' ? tenantFilter : undefined,
  }), [bbox, tenantFilter]);

  const { data: assetsData, isFetching: assetsFetching } = useQuery({
    ...getGetMapAssetsQueryOptions(assetsParams),
    enabled: effectiveShowAssets,
    staleTime: 60_000,
  });

  const scopedIssuesData = useMemo(() => {
    const collection = issuesData as IssueFeatureCollection | undefined;
    if (!collection || !purposeContext) return collection;
    return {
      ...collection,
      features: collection.features.filter((feature) => {
        const slug = feature.properties?.categorySlug;
        return typeof slug === 'string' && scopedCategorySlugs.has(slug);
      }),
    };
  }, [issuesData, purposeContext, scopedCategorySlugs]);

  // Any overlay refetch (pan/zoom, filters, assets toggle, address geocode) — not map-engine load.
  const overlaysFetching =
    issuesFetching
    || regionsFetching
    || (effectiveShowAssets && assetsFetching)
    || (shouldGeocodeAddress && geocodeFetching);

  regionsDataRef.current = regionsData as RegionFeatureCollection | undefined;
  issuesDataRef.current = scopedIssuesData;
  assetsDataRef.current = assetsData as AssetFeatureCollection | undefined;
  homeRentalPropertiesRef.current = homeRentalProperties;
  showAssetsRef.current = effectiveShowAssets;
  showCouncilBoundariesRef.current = effectiveShowCouncilBoundaries;
  showFarmBoundariesRef.current = effectiveShowFarmBoundaries;
  tenantByIdRef.current = tenantById;

  /** Push the latest overlay payloads into map sources once style + sources exist. */
  const syncMapSources = useCallback((map: MaplibreMap) => {
    if (!map.isStyleLoaded()) return;

    const regionsSource = map.getSource('regions') as GeoJSONSource | undefined;
    if (regionsSource) {
      regionsSource.setData(
        toRegionCollection(regionsDataRef.current, tenantByIdRef.current),
      );
    }

    const issuesSource = map.getSource('issues') as GeoJSONSource | undefined;
    if (issuesSource) {
      issuesSource.setData(toIssueCollection(issuesDataRef.current));
    }

    const assetsSource = map.getSource('assets') as GeoJSONSource | undefined;
    if (assetsSource) {
      assetsSource.setData(toAssetCollection(assetsDataRef.current));
    }

    const homeRentalPropertiesSource = map.getSource('home-rental-properties') as GeoJSONSource | undefined;
    if (homeRentalPropertiesSource) {
      homeRentalPropertiesSource.setData(
        toHomeRentalPropertyCollection(homeRentalPropertiesRef.current),
      );
    }

    if (map.getLayer('assets-points')) {
      map.setLayoutProperty(
        'assets-points',
        'visibility',
        showAssetsRef.current ? 'visible' : 'none',
      );
    }

    const regionFilter = buildRegionLayerFilter(
      showCouncilBoundariesRef.current,
      showFarmBoundariesRef.current,
    );
    for (const layerId of ['regions-fill', 'regions-line', 'regions-labels'] as const) {
      if (map.getLayer(layerId)) {
        // MapLibre filter expression; cast keeps TS happy across FilterSpecification variants.
        map.setFilter(layerId, regionFilter as never);
      }
    }
  }, []);

  // Region summary
  const { data: regionSummary, isLoading: regionSummaryLoading } = useQuery({
    ...getGetRegionSummaryQueryOptions(selectedRegionId ?? '00000000-0000-0000-0000-000000000000'),
    enabled: !!selectedRegionId,
  });

  // Deep-link: open a region by id or slug from the query string (e.g. /map?regionSlug=circle-t-farm).
  useEffect(() => {
    if (regionParam) {
      setSelectedRegionId(regionParam);
      return;
    }
    if (!regionSlugParam) return;
    const features = (regionsData as RegionFeatureCollection | undefined)?.features ?? [];
    const match = features.find((f) => f.properties?.slug === regionSlugParam);
    if (match?.properties?.id) {
      setSelectedRegionId(match.properties.id);
    }
  }, [regionParam, regionSlugParam, regionsData]);

  // Smoothly frame the selected region once its boundary and the map are available
  // (deep-links from farm/house "View on Map", and click selection fallback).
  useEffect(() => {
    if (!selectedRegionId) {
      fittedRegionIdRef.current = null;
      return;
    }
    if (fittedRegionIdRef.current === selectedRegionId) return;

    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    let boundary: unknown = regionSummary?.boundary;
    if (!boundary) {
      const feature = (regionsData as RegionFeatureCollection | undefined)?.features?.find(
        (f) => f.properties?.id === selectedRegionId,
      );
      boundary = feature?.geometry;
    }
    if (!frameGeometryOnMap(map, boundary)) return;

    fittedRegionIdRef.current = selectedRegionId;
  }, [selectedRegionId, regionSummary, regionsData, mapReadyVersion]);

  // Point deep-link: fly to lat/lng (or geocoded address) once the map is ready.
  // Region deep-links take priority so farm "View on Map" framing is unchanged.
  useEffect(() => {
    if (!deepLinkLocation) {
      fittedLocationKeyRef.current = null;
      return;
    }
    if (fittedLocationKeyRef.current === deepLinkLocation.key) return;

    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    flyMapToPoint(map, deepLinkLocation.point, deepLinkLocation.zoom);
    setBbox(bboxAroundPoint(deepLinkLocation.point));
    fittedLocationKeyRef.current = deepLinkLocation.key;
  }, [deepLinkLocation, mapReadyVersion]);

  // ── Filter helpers ─────────────────────────────────────────────────────

  const updateFilter = useCallback((key: string, value: string, options?: { closeMobileFilters?: boolean }) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all' || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
    // Collapse the mobile filter sheet after a discrete choice so the map stays usable.
    if (options?.closeMobileFilters !== false && (key === 'status' || key === 'category' || key === 'tenant')) {
      setShowFiltersMobile(false);
    }
  }, [searchParams, setSearchParams]);

  // ── Map initialization ─────────────────────────────────────────────────
  // Independent of filters/bbox/query data. Create → on ready → add layers.
  // Overlays sync through a separate effect once sources exist.

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    setLoading(true);
    setMapError(null);

    let disposed = false;
    let map: MaplibreMap | null = null;
    let layersReady = false;

    // Guarantee a paint box before MapLibre samples clientWidth/Height.
    // Full-page flex shells and the CDF preview iframe can still be 0×0
    // on the first paint frame.
    if (container.clientWidth < 2 || container.clientHeight < 2) {
      container.style.width = '100%';
      container.style.minHeight = '50vh';
      container.style.height = '100%';
    }

    // Prefer URL coords (or a resolved geocode) so first paint is already on-target.
    const startPoint = deepLinkLocation?.point ?? urlCoords;
    const startCenter: [number, number] = startPoint
      ? [startPoint.lng, startPoint.lat]
      : DEFAULT_CENTER;
    const startZoom = deepLinkLocation?.zoom
      ?? (urlCoords ? urlZoom : DEFAULT_ZOOM);

    try {
      map = createOsmMap(container, {
        center: startCenter,
        zoom: startZoom,
      });
    } catch (err) {
      setMapError(
        `The map engine failed to initialize: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
      setLoading(false);
      return;
    }

    mapRef.current = map;

    const setupLayers = () => {
      if (disposed || !map || layersReady) return;
      // Watchdog / early callbacks must wait until the style is usable.
      if (!map.isStyleLoaded()) {
        map.once('load', setupLayers);
        return;
      }
      // Sources may already exist after a hot-reload edge case — skip if so.
      if (map.getSource('regions')) {
        layersReady = true;
        setLoading(false);
        setMapReadyVersion((v) => v + 1);
        syncMapSources(map);
        return;
      }
      layersReady = true;
      setLoading(false);
      setMapReadyVersion((v) => v + 1);
      resizeOsmMap(map);

      // Seed bbox from the live viewport (overrides DEFAULT_BBOX once the map is real).
      const bounds = map.getBounds();
      setBbox({
        minLng: bounds.getWest(),
        minLat: bounds.getSouth(),
        maxLng: bounds.getEast(),
        maxLat: bounds.getNorth(),
      });

      // Region boundaries source
      map.addSource('regions', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      } as GeoJSONSourceSpecification);

      map.addLayer({
        id: 'regions-fill',
        type: 'fill',
        source: 'regions',
        paint: {
          // Farms (amber) sit visually above faint council (indigo) fills.
          'fill-color': [
            'match',
            ['get', 'regionKind'],
            'farm',
            FARM_BOUNDARY_COLOR,
            COUNCIL_BOUNDARY_COLOR,
          ],
          'fill-opacity': [
            'match',
            ['get', 'regionKind'],
            'farm',
            0.22,
            0.06,
          ],
        },
      } as FillLayerSpecification);

      map.addLayer({
        id: 'regions-line',
        type: 'line',
        source: 'regions',
        paint: {
          'line-color': [
            'match',
            ['get', 'regionKind'],
            'farm',
            FARM_BOUNDARY_COLOR,
            COUNCIL_BOUNDARY_COLOR,
          ],
          'line-width': [
            'match',
            ['get', 'regionKind'],
            'farm',
            3,
            2,
          ],
          'line-opacity': 0.95,
        },
      } as LineLayerSpecification);

      map.addLayer({
        id: 'regions-labels',
        type: 'symbol',
        source: 'regions',
        layout: {
          'text-field': ['get', 'name'],
          'text-size': [
            'match',
            ['get', 'regionKind'],
            'farm',
            14,
            12,
          ],
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.05,
          'text-max-width': 10,
        },
        paint: {
          'text-color': [
            'match',
            ['get', 'regionKind'],
            'farm',
            '#92400e',
            '#374151',
          ],
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      } as SymbolLayerSpecification);

      map.addSource('issues', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom: 14,
      } as GeoJSONSourceSpecification);

      map.addLayer({
        id: 'issues-clusters',
        type: 'circle',
        source: 'issues',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#6366f1',
          'circle-radius': ['step', ['get', 'point_count'], 18, 50, 28, 100, 38],
          'circle-opacity': 0.7,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        } as CircleLayerSpecification['paint'],
      } as CircleLayerSpecification);

      map.addLayer({
        id: 'issues-cluster-count',
        type: 'symbol',
        source: 'issues',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-size': 13,
        },
        paint: {
          'text-color': '#ffffff',
        },
      } as SymbolLayerSpecification);

      const matchExpr: unknown[] = ['match', ['get', 'status']];
      for (const [status, color] of Object.entries(STATUS_COLORS)) {
        matchExpr.push(status, color);
      }
      matchExpr.push('#6b7280');

      map.addLayer({
        id: 'issues-unclustered',
        type: 'circle',
        source: 'issues',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': matchExpr as CircleLayerSpecification['paint'] extends { 'circle-color': infer T } ? T : never,
          'circle-radius': 9,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        } as CircleLayerSpecification['paint'],
      } as CircleLayerSpecification);

      map.addSource('assets', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      } as GeoJSONSourceSpecification);

      map.addLayer({
        id: 'assets-points',
        type: 'circle',
        source: 'assets',
        paint: {
          'circle-color': '#f59e0b',
          'circle-radius': 5,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.8,
        },
        layout: { visibility: showAssetsRef.current ? 'visible' : 'none' },
      } as CircleLayerSpecification);

      map.addSource('home-rental-properties', {
        type: 'geojson',
        data: toHomeRentalPropertyCollection(homeRentalPropertiesRef.current),
      } as GeoJSONSourceSpecification);

      map.addLayer({
        id: 'home-rental-properties-points',
        type: 'circle',
        source: 'home-rental-properties',
        paint: {
          'circle-color': '#0f766e',
          'circle-radius': 9,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.95,
        },
      } as CircleLayerSpecification);

      // Apply any overlay data that arrived before style ready.
      syncMapSources(map);

      // ── Click handlers ────────────────────────────────────────────────

      map.on('click', 'issues-clusters', (e) => {
        if (!map) return;
        const features = map.queryRenderedFeatures(e.point, { layers: ['issues-clusters'] });
        if (features.length === 0) return;
        const f = features[0];
        if (!f) return;
        const geom = f.geometry as { coordinates: [number, number] } | undefined;
        if (!geom) return;
        map.easeTo({
          center: geom.coordinates,
          zoom: map.getZoom() + 2,
        });
      });

      map.on('click', 'issues-unclustered', (e) => {
        if (!map) return;
        const features = map.queryRenderedFeatures(e.point, { layers: ['issues-unclustered'] });
        if (features.length === 0) return;
        const f = features[0];
        if (!f) return;
        const props = f.properties ?? {};
        const geom = f.geometry as { coordinates: [number, number] } | undefined;
        if (!geom) return;
        const coords = geom.coordinates;

        const status = props.status as string;
        const sc = getStatusConfig(status);
        const photoUrl = props.photoUrl as string | null;
        const severity = props.severity as string;
        const categoryName = (props.categoryName as string) ?? 'Uncategorized';
        const upvoteCount = (props.upvoteCount as number) ?? 0;
        const createdAt = props.createdAt as string;
        const trackingToken = props.trackingToken as string;
        const tenantName = (props.tenantName as string) ?? 'Unassigned';

        const latStr = coords[1].toFixed(5);
        const lngStr = coords[0].toFixed(5);

        const html = `
          <div style="font-family: inherit; max-width: 240px;">
            ${photoUrl ? `<img src="${photoUrl}" alt="Report photo" style="width:100%;height:120px;object-fit:cover;border-radius:6px;margin-bottom:8px;" />` : ''}
            <div style="font-weight:600;font-size:14px;margin-bottom:4px;">${categoryName}</div>
            <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:6px;">
              <span style="background:${STATUS_COLORS[status] ?? '#6b7280'};color:#fff;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600;">${sc.label}</span>
              <span style="background:#e5e7eb;color:#374151;padding:2px 8px;border-radius:9999px;font-size:11px;">${SEVERITY_LABELS[severity as keyof typeof SEVERITY_LABELS] ?? severity}</span>
            </div>
            <div style="font-size:12px;color:#6b7280;margin-bottom:4px;">${tenantName} · ${upvoteCount} supports · ${formatRelativeTime(createdAt)}</div>
            ${photoUrl ? `<div style="font-size:11px;color:#6b7280;margin-bottom:4px;">📸 Photo location: ${latStr}, ${lngStr}</div>` : `<div style="font-size:11px;color:#6b7280;margin-bottom:4px;">📍 ${latStr}, ${lngStr}</div>`}
            <a href="/track/${trackingToken}" style="display:inline-block;margin-top:6px;color:#4f46e5;font-size:13px;font-weight:600;text-decoration:none;">View tracking page →</a>
          </div>`;

        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new Popup({ maxWidth: '280px', closeButton: true })
          .setLngLat(coords)
          .setHTML(html)
          .addTo(map);
      });

      map.on('click', 'regions-fill', (e) => {
        if (!map) return;
        const features = map.queryRenderedFeatures(e.point, { layers: ['regions-fill'] });
        if (features.length === 0) return;
        // Prefer farm parcels when stacked under a larger LGA polygon.
        const preferred =
          features.find((f) => f.properties?.regionKind === 'farm') ?? features[0];
        if (!preferred) return;
        const regionId = preferred.properties?.id as string | undefined;
        if (!regionId) return;

        // Always fly to the clicked farm/house/region, even when re-selecting
        // the same parcel after the user has panned away.
        fittedRegionIdRef.current = null;
        if (preferred.geometry && frameGeometryOnMap(map, preferred.geometry)) {
          fittedRegionIdRef.current = regionId;
        }
        setSelectedRegionId(regionId);
      });

      map.on('click', 'assets-points', (e) => {
        if (!map) return;
        const features = map.queryRenderedFeatures(e.point, { layers: ['assets-points'] });
        if (features.length === 0) return;
        const f = features[0];
        if (!f) return;
        const props = f.properties ?? {};
        const geom = f.geometry as { coordinates: [number, number] } | undefined;
        if (!geom) return;
        const coords = geom.coordinates;

        // Bring the asset (e.g. house/building) into view under the popup.
        map.easeTo({
          center: coords,
          duration: 500,
          // Keep current zoom unless very zoomed out so context stays readable.
          zoom: Math.max(map.getZoom(), 15),
        });

        const assetTypeName = (props.assetTypeName as string) ?? 'Unknown asset';
        const tenantName = (props.tenantName as string) ?? 'Unknown';
        const tenantType = (props.tenantType as string) ?? '';
        const condition = (props.condition as string) ?? 'unknown';
        const openIssues = (props.openIssueCount as number) ?? 0;
        const address = (props.address as string) ?? '';

        const html = `
          <div style="font-family: inherit; max-width: 220px;">
            <div style="font-weight:600;font-size:14px;margin-bottom:4px;">${assetTypeName}</div>
            <div style="font-size:12px;color:#6b7280;margin-bottom:4px;">${address}</div>
            <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:6px;">
              <span style="background:#e5e7eb;color:#374151;padding:2px 8px;border-radius:9999px;font-size:11px;">${condition}</span>
              <span style="background:${tenantType === 'council' ? '#dbeafe' : '#fef3c7'};color:${tenantType === 'council' ? '#1e40af' : '#92400e'};padding:2px 8px;border-radius:9999px;font-size:11px;">${tenantName}</span>
            </div>
            ${openIssues > 0 ? `<div style="font-size:12px;color:#ef4444;font-weight:600;">${openIssues} open issue${openIssues > 1 ? 's' : ''}</div>` : '<div style="font-size:12px;color:#22c55e;">No open issues</div>'}
          </div>`;

        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new Popup({ maxWidth: '260px', closeButton: true })
          .setLngLat(coords)
          .setHTML(html)
          .addTo(map);

        const clickedAssetId = props.id as string | undefined;
        const targetId = targetAssetIdRef.current;
        if (targetId && clickedAssetId && targetId === clickedAssetId) {
          const challenge = incompleteByTypeRef.current;
          const challengeId = challenge.map_find?.id;
          if (challengeId) {
            completeHuntChallengeRef.current(challengeId, { foundAssetId: clickedAssetId });
          }
        }
      });

      map.on('click', 'home-rental-properties-points', (e) => {
        if (!map) return;
        const feature = e.features?.[0];
        if (!feature) return;
        const coords = feature.geometry.type === 'Point'
          ? feature.geometry.coordinates as [number, number]
          : null;
        if (!coords) return;
        const name = String(feature.properties?.name ?? 'Home or rental property');
        const address = String(feature.properties?.address ?? '');
        const precision = feature.properties?.geocodePrecision === 'building-centroid'
          ? 'Building-centre location'
          : 'Property location';

        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new Popup({ maxWidth: '260px', closeButton: true })
          .setLngLat(coords)
          .setHTML(`
            <div style="font-family: inherit; max-width: 220px;">
              <div style="font-weight:600;font-size:14px;margin-bottom:4px;">${name}</div>
              <div style="font-size:12px;color:#6b7280;margin-bottom:6px;">${address}</div>
              <div style="font-size:11px;color:#0f766e;font-weight:600;">${precision}</div>
            </div>`)
          .addTo(map);
      });

      const cursorOn = () => {
        if (map) map.getCanvas().style.cursor = 'pointer';
      };
      const cursorOff = () => {
        if (map) map.getCanvas().style.cursor = '';
      };
      map.on('mouseenter', 'issues-unclustered', cursorOn);
      map.on('mouseleave', 'issues-unclustered', cursorOff);
      map.on('mouseenter', 'issues-clusters', cursorOn);
      map.on('mouseleave', 'issues-clusters', cursorOff);
      map.on('mouseenter', 'regions-fill', cursorOn);
      map.on('mouseleave', 'regions-fill', cursorOff);
      map.on('mouseenter', 'assets-points', cursorOn);
      map.on('mouseleave', 'assets-points', cursorOff);
      map.on('mouseenter', 'home-rental-properties-points', cursorOn);
      map.on('mouseleave', 'home-rental-properties-points', cursorOff);

      map.on('moveend', () => {
        if (moveTimerRef.current) clearTimeout(moveTimerRef.current);
        moveTimerRef.current = setTimeout(() => {
          if (!map) return;
          const b = map.getBounds();
          setBbox({
            minLng: b.getWest(),
            minLat: b.getSouth(),
            maxLng: b.getEast(),
            maxLat: b.getNorth(),
          });
        }, 300);
      });
    };

    // Handle both "load still pending" and "style already ready" (inline OSM style
    // can finish before the listener is attached in some browsers).
    map.on('load', setupLayers);
    if (map.loaded()) {
      setupLayers();
    }
    // Failsafe: never leave the spinner forever if load is delayed.
    const loadWatchdog = window.setTimeout(() => {
      if (!disposed && !layersReady) {
        setupLayers();
        resizeOsmMap(map!);
      }
    }, 2500);

    const handleViewportResize = () => {
      if (map) resizeOsmMap(map);
    };
    const resizeObserver = new ResizeObserver(handleViewportResize);
    resizeObserver.observe(container);
    window.addEventListener('orientationchange', handleViewportResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    return () => {
      disposed = true;
      window.clearTimeout(loadWatchdog);
      resizeObserver.disconnect();
      window.removeEventListener('orientationchange', handleViewportResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
      if (moveTimerRef.current) clearTimeout(moveTimerRef.current);
      if (popupRef.current) popupRef.current.remove();
      if (map) {
        map.remove();
      }
      mapRef.current = null;
    };
  }, [mapInitKey, syncMapSources]);

  // ── Keep sources in sync with query data (works before/after style load) ─

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    syncMapSources(map);
  }, [
    regionsData,
    scopedIssuesData,
    assetsData,
    homeRentalProperties,
    effectiveShowAssets,
    effectiveShowCouncilBoundaries,
    effectiveShowFarmBoundaries,
    tenantById,
    syncMapSources,
  ]);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-0 flex h-dvh max-h-dvh flex-col overflow-hidden bg-background">
      {/* Top bar with filters */}
      <div className="relative z-20 shrink-0 border-b bg-background px-2 py-2 sm:px-4 sm:py-3">
        <div className="flex min-h-11 items-center gap-1.5 sm:gap-3">
          <Link
            to="/"
            className="inline-flex h-11 min-w-11 shrink-0 items-center justify-center gap-1 rounded-md text-sm text-muted-foreground hover:text-foreground sm:h-auto sm:min-w-0 sm:justify-start sm:px-1"
            aria-label="Back to home"
          >
            <ArrowLeftIcon className="size-4 shrink-0" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          <h1 className="flex min-w-0 items-center gap-1.5 text-base font-semibold sm:gap-2 sm:text-lg">
            <MapIcon className="size-4 shrink-0 sm:size-5" />
            <span className="truncate">
              {purposeContext ? `${purposeContext.shortName} Map` : 'Map'}
            </span>
          </h1>
          {/* Desktop inline filters */}
          <div className="hidden flex-1 flex-wrap items-center gap-2 md:flex">
            <FilterSelect
              value={statusFilter}
              onChange={(v) => updateFilter('status', v)}
              options={ALL_STATUS_OPTIONS}
              placeholder="All Statuses"
            />
            <FilterSelect
              value={effectiveCategoryFilter}
              onChange={(v) => updateFilter('category', v)}
              options={[
                { value: 'all', label: 'All Categories' },
                ...scopedCategories.map((category) => ({ value: category.id ?? '', label: category.name ?? '' })),
              ]}
              placeholder="All Categories"
            />
            <FilterSelect
              value={tenantFilter}
              onChange={(v) => updateFilter('tenant', v)}
              options={[
                { value: 'all', label: 'All Authorities' },
                ...tenants.map((tenant) => ({ value: tenant.id ?? '', label: tenant.name ?? '' })),
              ]}
              placeholder="All Authorities"
            />
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => updateFilter('from', e.target.value, { closeMobileFilters: false })}
              className="h-9 w-[140px]"
              aria-label="Date from"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => updateFilter('to', e.target.value, { closeMobileFilters: false })}
              className="h-9 w-[140px]"
              aria-label="Date to"
            />
          </div>
          {/* Spacer pushes controls right on mobile */}
          <div className="min-w-0 flex-1 md:hidden" />
          {/* Mobile filter toggle */}
          <Button
            type="button"
            size="sm"
            variant={showFiltersMobile ? 'secondary' : 'outline'}
            className="h-11 shrink-0 gap-1 px-2.5 md:hidden"
            aria-expanded={showFiltersMobile}
            aria-controls="map-mobile-filters"
            onClick={() => setShowFiltersMobile((v) => !v)}
          >
            <SlidersHorizontalIcon className="size-4" />
            <span className="text-xs sm:text-sm">Filters</span>
          </Button>
          <Button asChild size="sm" variant="outline" className="h-11 shrink-0 px-2.5 sm:px-3">
            <Link to="/ar" aria-label="Open AR view">
              <SmartphoneIcon className="size-4 sm:mr-1" />
              <span className="hidden sm:inline">AR</span>
            </Link>
          </Button>
        </div>
        {/* Mobile collapsible filters */}
        {showFiltersMobile && (
          <div
            id="map-mobile-filters"
            className="mt-2 space-y-2 border-t pt-2 md:hidden"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-muted-foreground">Filter map layers</p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-11 gap-1 px-2"
                onClick={() => setShowFiltersMobile(false)}
              >
                <XIcon className="size-4" />
                Close
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FilterSelect
                value={statusFilter}
                onChange={(v) => updateFilter('status', v)}
                options={ALL_STATUS_OPTIONS}
                placeholder="All Statuses"
                className="!h-11 w-full"
              />
              <FilterSelect
                value={effectiveCategoryFilter}
                onChange={(v) => updateFilter('category', v)}
                options={[
                  { value: 'all', label: 'All Categories' },
                  ...scopedCategories.map((category) => ({ value: category.id ?? '', label: category.name ?? '' })),
                ]}
                placeholder="All Categories"
                className="!h-11 w-full"
              />
              <FilterSelect
                value={tenantFilter}
                onChange={(v) => updateFilter('tenant', v)}
                options={[
                  { value: 'all', label: 'All Authorities' },
                  ...tenants.map((tenant) => ({ value: tenant.id ?? '', label: tenant.name ?? '' })),
                ]}
                placeholder="All Authorities"
                className="!h-11 w-full"
              />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => updateFilter('from', e.target.value, { closeMobileFilters: false })}
                className="h-11 w-full"
                aria-label="Date from"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => updateFilter('to', e.target.value, { closeMobileFilters: false })}
                className="h-11 w-full"
                aria-label="Date to"
              />
              <Button
                type="button"
                size="sm"
                className="h-11 w-full"
                onClick={() => setShowFiltersMobile(false)}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Map container — min-h-0 + absolute fill so MapLibre gets a real size in flex shells */}
      <div className="relative min-h-0 flex-1">
        <div
          ref={containerRef}
          className="absolute inset-0 h-full w-full min-h-[240px] touch-pan-x touch-pan-y"
          data-testid="map-explorer-canvas"
        />

        {/* Hunt map_find hint chip */}
        {mapFindChallenge && (
          <div className="pointer-events-none absolute left-1/2 top-3 z-10 max-w-[calc(100%-1.5rem)] -translate-x-1/2">
            <div className="flex items-center gap-2 rounded-full border bg-card/95 px-3 py-1.5 text-xs shadow-md backdrop-blur">
              <MapIcon className="h-3.5 w-3.5 shrink-0 text-amber-500" />
              <span className="truncate font-medium">Hunt: find the target asset</span>
              <span className="shrink-0 text-muted-foreground">+{mapFindChallenge.points} pts</span>
            </div>
          </div>
        )}

        {/* Overlay data fetch indicator (issues / assets / regions) */}
        {overlaysFetching && !loading && !mapError && (
          <div
            className={`pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 ${mapFindChallenge ? 'top-14' : 'top-3'}`}
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center gap-2 rounded-full border bg-card/95 px-3 py-1.5 text-xs shadow-md backdrop-blur">
              <Loader2Icon className="size-3.5 animate-spin text-primary" />
              <span className="font-medium">Updating map…</span>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {mapError && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 bg-background/90 p-6 text-center">
            <AlertCircleIcon className="size-10 text-destructive" />
            <div>
              <p className="font-semibold">The map could not be loaded on this device</p>
              <p className="mt-1 text-sm text-muted-foreground">{mapError}</p>
            </div>
            <Button onClick={handleReloadMap} variant="outline" className="h-11">
              <RotateCcwIcon className="mr-1 size-4" />
              Reload map
            </Button>
          </div>
        )}

        {/* Loading overlay */}
        {loading && !mapError && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/80">
            <Loader2Icon className="size-8 animate-spin text-primary" />
          </div>
        )}

        <ZoomPanControls
          onZoomIn={() => handleMapZoom(1)}
          onZoomOut={() => handleMapZoom(-1)}
          onPan={handleMapPan}
          onReset={handleMapReset}
          className="bottom-36 sm:bottom-40"
        />

        {/* Bottom chrome: legend + assets — gaps stay pointer-transparent for map gestures */}
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-2 px-3',
            'pb-[max(0.75rem,env(safe-area-inset-bottom))]',
          )}
        >
          {/* Legend — collapsible on small screens */}
          <Card className="pointer-events-auto max-w-[min(200px,46vw)] py-0 shadow-lg">
            <CardContent className="px-2 py-1.5 sm:px-3 sm:py-2">
              <button
                type="button"
                className="flex h-10 w-full items-center justify-between gap-2 text-left md:h-auto md:cursor-default"
                aria-expanded={legendExpanded}
                onClick={() => setLegendExpanded((v) => !v)}
              >
                <span className="text-xs font-semibold">Issue Status</span>
                <span className="md:hidden">
                  {legendExpanded ? (
                    <ChevronDownIcon className="size-4 text-muted-foreground" />
                  ) : (
                    <ChevronUpIcon className="size-4 text-muted-foreground" />
                  )}
                </span>
              </button>
              <div
                className={cn(
                  'space-y-1.5 pt-1',
                  // Expanded by default on desktop; collapsed by default on mobile
                  legendExpanded ? 'block' : 'hidden md:block',
                )}
              >
                {STATUS_GROUPS.map((group) => (
                  <div key={group.label} className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {group.colors.map((c) => (
                        <div key={c} className="size-2.5 rounded-full" style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">{group.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Layer toggles — assets + council/farm boundaries */}
          <Card className="pointer-events-auto shrink-0 py-0 shadow-lg">
            <CardContent className="space-y-1.5 px-2 py-1.5 sm:px-3 sm:py-2">
              <div className="flex items-center gap-1.5 pb-0.5">
                <LayersIcon className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Layers
                </span>
              </div>
              {(regionalMapScope === null || regionalMapScope === 'council') && (
                <div className="flex h-9 items-center gap-2 sm:h-auto">
                  <span
                    className="size-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: COUNCIL_BOUNDARY_COLOR }}
                    aria-hidden
                  />
                  <Label htmlFor="council-boundary-toggle" className="cursor-pointer text-xs">
                    Councils
                  </Label>
                  <Switch
                    id="council-boundary-toggle"
                    checked={effectiveShowCouncilBoundaries}
                    onCheckedChange={setShowCouncilBoundaries}
                    className="ml-auto scale-110"
                  />
                </div>
              )}
              {(regionalMapScope === null || regionalMapScope === 'farm') && (
                <div className="flex h-9 items-center gap-2 sm:h-auto">
                  <span
                    className="size-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: FARM_BOUNDARY_COLOR }}
                    aria-hidden
                  />
                  <Label htmlFor="farm-boundary-toggle" className="cursor-pointer text-xs">
                    Farms
                  </Label>
                  <Switch
                    id="farm-boundary-toggle"
                    checked={effectiveShowFarmBoundaries}
                    onCheckedChange={setShowFarmBoundaries}
                    className="ml-auto scale-110"
                  />
                </div>
              )}
              {regionalMapScope === null && (
                <div className="flex h-9 items-center gap-2 sm:h-auto">
                  <span
                    className="size-2.5 shrink-0 rounded-full bg-amber-500"
                    aria-hidden
                  />
                  <Label htmlFor="asset-toggle" className="cursor-pointer text-xs">
                    Assets
                  </Label>
                  <Switch
                    id="asset-toggle"
                    checked={effectiveShowAssets}
                    onCheckedChange={setShowAssets}
                    className="ml-auto scale-110"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Region summary panel */}
        {selectedRegionId && (
          <RegionSummaryPanel
            summary={regionSummary}
            loading={regionSummaryLoading}
            onClose={() => {
              fittedRegionIdRef.current = null;
              setSelectedRegionId(null);
              // Drop deep-link params so closing the panel sticks on refresh.
              if (regionParam || regionSlugParam) {
                const next = new URLSearchParams(searchParams);
                next.delete('region');
                next.delete('regionSlug');
                setSearchParams(next, { replace: true });
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Filter Select component ─────────────────────────────────────────────────

interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  className?: string;
}

function FilterSelect({ value, onChange, options, placeholder, className }: FilterSelectProps) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" className={cn('h-9 w-[150px]', className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Region summary panel ────────────────────────────────────────────────────

function RegionSummaryPanel({
  summary,
  loading,
  onClose,
}: {
  summary: RegionSummary | undefined;
  loading: boolean;
  onClose: () => void;
}) {
  const slug = summary?.slug ?? '';
  const isFarm =
    /farm/i.test(slug) || /farm/i.test(summary?.name ?? '') || slug === 'circle-t-farm';
  const address = getFarmAddress(slug);

  return (
    <Card
      className={cn(
        'absolute z-20 flex max-h-[min(70dvh,calc(100%-5.5rem))] w-auto flex-col shadow-xl',
        // Mobile: bottom sheet-style card above the legend/assets chrome
        'inset-x-3 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] top-auto max-w-none',
        // Desktop: top-right floating card
        'md:inset-x-auto md:bottom-auto md:right-4 md:top-4 md:w-[280px] md:max-w-[calc(100vw-2rem)]',
      )}
    >
      <CardHeader className="flex shrink-0 flex-row items-center justify-between space-y-0 pb-2">
        <div className="min-w-0 pr-2">
          <CardTitle className="truncate text-sm font-semibold">
            {loading ? 'Loading…' : summary?.name ?? 'Region'}
          </CardTitle>
          {isFarm && !loading && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <WheatIcon className="size-3" />
              Farm property
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-11 shrink-0 md:size-8"
          onClick={onClose}
          aria-label="Close region summary"
        >
          <XIcon className="size-4" />
        </Button>
      </CardHeader>
      <CardContent className="min-h-0 space-y-3 overflow-y-auto overscroll-contain pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : summary ? (
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Open" value={summary.openIssueCount} color="text-red-500" />
              <Stat label="Total" value={summary.totalIssueCount} color="text-blue-500" />
              <Stat label="Assets" value={summary.assetCount} color="text-amber-500" />
            </div>
            {address && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <MapPinIcon className="mt-0.5 size-3.5 shrink-0 text-amber-700" />
                <span>{address}</span>
              </div>
            )}
            {summary.tenantName && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {isFarm ? (
                  <WheatIcon className="size-3.5 shrink-0 text-amber-700" />
                ) : (
                  <Building2Icon className="size-3.5 shrink-0" />
                )}
                <span className="truncate">{summary.tenantName}</span>
              </div>
            )}
            <div className="flex flex-col gap-2 pt-1">
              <Button asChild size="sm" variant="outline" className="h-11 md:h-9">
                <Link to={`/report?purpose=${isFarm ? 'farms' : 'councils'}&region=${summary.id}`}>
                  {isFarm ? 'Report farm issue' : 'Report here'}
                  <ChevronRightIcon className="size-3.5" />
                </Link>
              </Button>
              <Button asChild size="sm" variant="secondary" className="h-11 md:h-9">
                <Link to={`/twin/locations/${summary.slug}`}>
                  <BoxIcon className="size-3.5" />
                  View 3D Digital Twin
                  <ChevronRightIcon className="size-3.5" />
                </Link>
              </Button>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-md bg-muted p-2">
      <div className={cn('text-lg font-bold', color)}>{value}</div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
    </div>
  );
}
